import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';

import type { IResponse as IClientResponse } from 'api/endpoints/useGetClient';

import styles from 'theme/components/containers/TraderClientCardContainer/TraderClientData/TraderClientData.module.scss';

interface IClientWithNip extends IClientResponse {
  tax_number: string;
}

interface ITraderClientDataProps {
  client?: IClientWithNip | null;
  isLoading?: boolean;
}

const formatAddress = (address: IClientResponse['address']): string => {
  if (!address) return '–';
  const buildingAndApartment =
    address.house_number && address.apartment_number
      ? `${address.house_number}/${address.apartment_number}`
      : address.house_number || address.apartment_number || '';
  const parts = [
    [address.street, buildingAndApartment].filter(Boolean).join(' '),
    [address.postal_code, address.city].filter(Boolean).join(' '),
    address.state ?? address.country ?? ''
  ].filter(Boolean);
  return parts.join(', ') || '–';
};

const TraderClientData: React.FC<ITraderClientDataProps> = ({ client, isLoading = false }) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div
        className={classnames(styles.wrapper, 'StylePath-Containers-TraderClientCardContainer-TraderClientData')}>
        <section className={styles.section}>
          <Skeleton variant="text" width={120} height={24} sx={{ mb: 1.5 }} />
          <Skeleton variant="text" width="80%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="40%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="60%" height={20} />
        </section>
        <section className={styles.section}>
          <Skeleton variant="text" width={80} height={24} sx={{ mb: 1.5 }} />
          <Skeleton variant="text" width="70%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="50%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="90%" height={20} />
        </section>
      </div>
    );
  }

  if (!client) {
    return null;
  }

  const fullName = [client.first_name, client.last_name].filter(Boolean).join(' ') || client.name || '–';
  const nip = client.tax_number ?? '–';
  const address = formatAddress(client.address);

  return (
    <div
      className={classnames(styles.wrapper, 'StylePath-Containers-TraderClientCardContainer-TraderClientData')}>
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('Dane klienta')}</h3>
        <p className={styles.row}>
          <span className={styles.label}>{t('Imię i nazwisko')}:</span>{' '}
          <span className={styles.value}>{fullName}</span>
        </p>
        <p className={styles.row}>
          <span className={styles.label}>{t('Pełna nazwa')}:</span>{' '}
          <span className={styles.value}>{client.name}</span>
        </p>
        <p className={styles.row}>
          <span className={styles.label}>{t('Id klienta')}:</span>{' '}
          <span className={styles.value}>{client.id}</span>
        </p>
        <p className={styles.row}>
          <span className={styles.label}>{t('NIP')}:</span>{' '}
          <span className={styles.value}>{nip}</span>
        </p>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('Kontakt')}</h3>
        <p className={styles.row}>
          <span className={styles.label}>{t('Email')}:</span>{' '}
          <span className={styles.value}>{client.email || '–'}</span>
        </p>
        <p className={styles.row}>
          <span className={styles.label}>{t('Telefon')}:</span>{' '}
          <span className={styles.value}>{client.phone || '–'}</span>
        </p>
        <p className={styles.row}>
          <span className={styles.label}>{t('Adres')}:</span>{' '}
          <span className={styles.value}>{address}</span>
        </p>
      </section>
    </div>
  );
};

export default TraderClientData;
